/**
 * Deal Flow Column Definitions
 *
 * Shared DataGrid column definitions for Deal Flow features.
 * Use these in campaign list tables to display funding stage.
 *
 * @example
 * import { getDealFlowColumns } from "../../utils/dealFlowColumns";
 * const dealFlowCols = getDealFlowColumns();
 * const columns = [...userColumns, ...dealFlowCols, ...actionColumn];
 */

import React from "react";
import { Chip } from "@mui/material";

/**
 * Funding stage color mapping
 * Each stage has a background and text color for visual distinction
 */
const FUNDING_STAGE_COLORS = {
  PRE_SEED: { bg: "#e3f2fd", color: "#1565c0" },
  SEED: { bg: "#e8f5e9", color: "#2e7d32" },
  SERIES_A: { bg: "#fff3e0", color: "#ef6c00" },
  SERIES_B: { bg: "#fce4ec", color: "#c2185b" },
  SERIES_C: { bg: "#f3e5f5", color: "#7b1fa2" },
  GROWTH: { bg: "#e0f2f1", color: "#00695c" },
  BRIDGE: { bg: "#fff8e1", color: "#f57c00" },
  ENTERPRISE: { bg: "#efebe9", color: "#4e342e" },
};

/**
 * Normalize a funding stage value into its key (e.g., ENTERPRISE).
 * Handles i18n-style values like "deal_flow.funding_stages.ENTERPRISE".
 */
const normalizeFundingStageKey = (stage) => {
  if (!stage) return "";
  const raw = stage.toString();
  return raw.includes(".") ? raw.split(".").pop() : raw;
};

/**
 * Format a funding stage for display.
 */
const formatFundingStageLabel = (stage) => {
  const key = normalizeFundingStageKey(stage);
  return key ? key.replace(/_/g, " ") : "";
};

/**
 * Funding stage column definition
 * Displays colored chip based on funding stage
 */
const fundingStageColumn = {
  field: "funding_stage",
  headerName: "Stage",
  width: 120,
  renderCell: (params) => {
    const stageKey = normalizeFundingStageKey(params.row.funding_stage);
    if (!stageKey) {
      return <span style={{ color: "#999" }}>—</span>;
    }
    const colors = FUNDING_STAGE_COLORS[stageKey] || { bg: "#f5f5f5", color: "#666" };
    return (
      <Chip
        label={formatFundingStageLabel(stageKey)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 500,
          fontSize: "11px",
        }}
      />
    );
  },
};

/**
 * Get Deal Flow columns for DataGrid
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeFundingStage - Include funding stage column (default: true)
 * @returns {Array} Array of column definitions
 */
export const getDealFlowColumns = (options = {}) => {
  const { includeFundingStage = true } = options;
  const columns = [];

  if (includeFundingStage) {
    columns.push(fundingStageColumn);
  }

  return columns;
};

/**
 * Individual column exports for more granular control
 */
export { fundingStageColumn, FUNDING_STAGE_COLORS, normalizeFundingStageKey, formatFundingStageLabel };

export default getDealFlowColumns;
